import * as THREE from 'three';
import { materialPool } from '../game/MaterialPool';
import type { MissileLaunchEvent } from '@shared/types';
import type { RemotePlayerManager } from './RemotePlayerManager';
import type { FlareSystem } from '../game/FlareSystem';
import type { ExplosionSystem } from '../game/Explosion';

// ============================================================
// Remote tracers — visual-only bullets from remote F-16s
// ============================================================

const TRACER_SPEED = 800;
const TRACER_LIFETIME = 0.7;
const TRACER_LENGTH = 60;
const TRACER_RADIUS = 0.18;
const FIRE_INTERVAL = 0.05;
const SPREAD = 0.035;

const tracerGeometry = new THREE.CylinderGeometry(TRACER_RADIUS, TRACER_RADIUS, TRACER_LENGTH, 4, 1);
tracerGeometry.rotateX(Math.PI / 2);

interface RemoteTracer {
  readonly mesh: THREE.Mesh;
  readonly velocity: THREE.Vector3;
  age: number;
}

// ============================================================
// Remote missiles — full local simulation on the drone's client
// Uses real missile constants for accurate gameplay
// ============================================================

const MISSILE_SPEED = 300;       // matches MissileSystem
const MISSILE_TURN_RATE = 2.0;   // matches MissileSystem
const MISSILE_LIFETIME = 7;      // matches MissileSystem
const MISSILE_HIT_RADIUS = 8;    // distance to count as a hit on local player

const missileGeometry = new THREE.CylinderGeometry(0.4, 0.12, 4, 6, 1);
missileGeometry.rotateX(Math.PI / 2);

interface RemoteMissile {
  readonly id: string;
  readonly mesh: THREE.Mesh;
  readonly targetId: string;
  readonly direction: THREE.Vector3;
  age: number;
  decoyed: boolean; // true = chasing a flare, lost target
}

// Reusable vectors
const _forward = new THREE.Vector3();
const _right = new THREE.Vector3();
const _up = new THREE.Vector3();
const _toTarget = new THREE.Vector3();
const _lookTarget = new THREE.Vector3();

// ============================================================
// Callbacks for hit events
// ============================================================

export interface RemoteProjectileCallbacks {
  /** Called when a remote missile hits the local player */
  readonly onMissileHitLocal: (missileId: string, position: THREE.Vector3) => void;
}

// ============================================================
// Manager
// ============================================================

export class RemoteProjectileManager {
  private readonly scene: THREE.Scene;
  private readonly tracers: RemoteTracer[] = [];
  private readonly missiles: RemoteMissile[] = [];
  private readonly fireTimers = new Map<string, number>();
  private readonly remotePlayerManager: RemotePlayerManager;
  private readonly localPlayerId: string;
  private localPlayerMesh: THREE.Object3D | null = null;
  private flareSystem: FlareSystem | null = null;
  private explosions: ExplosionSystem | null = null;
  private callbacks: RemoteProjectileCallbacks | null = null;

  constructor(
    scene: THREE.Scene,
    remotePlayerManager: RemotePlayerManager,
    localPlayerId: string,
  ) {
    this.scene = scene;
    this.remotePlayerManager = remotePlayerManager;
    this.localPlayerId = localPlayerId;
  }

  setLocalPlayerMesh(mesh: THREE.Object3D): void {
    this.localPlayerMesh = mesh;
  }

  setFlareSystem(flareSystem: FlareSystem): void {
    this.flareSystem = flareSystem;
  }

  setExplosions(explosions: ExplosionSystem): void {
    this.explosions = explosions;
  }

  setCallbacks(callbacks: RemoteProjectileCallbacks): void {
    this.callbacks = callbacks;
  }

  /**
   * Call every frame. Spawns tracers for remote F-16s that are firing,
   * runs full local simulation for remote missiles.
   */
  update(dt: number): void {
    this.updateTracers(dt);
    this.updateMissiles(dt);
  }

  // ── Tracers ─────────────────────────────────────────────

  private updateTracers(dt: number): void {
    for (let i = this.tracers.length - 1; i >= 0; i--) {
      const t = this.tracers[i];
      t.age += dt;

      if (t.age >= TRACER_LIFETIME) {
        this.scene.remove(t.mesh);
        materialPool.release(t.mesh.material as THREE.MeshBasicMaterial);
        this.tracers.splice(i, 1);
        continue;
      }

      t.mesh.position.addScaledVector(t.velocity, dt);
      (t.mesh.material as THREE.MeshBasicMaterial).opacity = 0.9 * (1 - t.age / TRACER_LIFETIME);
    }
  }

  /**
   * Called from Game.ts when we receive a remoteStates update.
   * Checks which remote F-16s are firing and spawns tracers.
   */
  updateFiringStates(firingIds: Set<string>): void {
    const remotes = this.remotePlayerManager.getRemotes();

    for (const [id, entry] of remotes) {
      if (entry.role !== 'f16') continue;

      const timer = this.fireTimers.get(id) ?? 0;

      if (firingIds.has(id)) {
        const newTimer = timer - (1 / 20); // approx one tick at 20Hz
        if (newTimer <= 0) {
          this.spawnTracer(entry.mesh);
          this.fireTimers.set(id, FIRE_INTERVAL);
        } else {
          this.fireTimers.set(id, newTimer);
        }
      } else {
        this.fireTimers.set(id, 0);
      }
    }
  }

  private spawnTracer(aircraft: THREE.Object3D): void {
    _forward.set(0, 0, -1).applyQuaternion(aircraft.quaternion);
    _right.set(1, 0, 0).applyQuaternion(aircraft.quaternion);
    _up.set(0, 1, 0).applyQuaternion(aircraft.quaternion);

    const dir = _forward.clone();
    dir.addScaledVector(_right, (Math.random() - 0.5) * SPREAD);
    dir.addScaledVector(_up, (Math.random() - 0.5) * SPREAD);
    dir.normalize();

    const velocity = dir.clone().multiplyScalar(TRACER_SPEED);

    const mat = materialPool.acquire();
    mat.color.set(0xffaa00);
    mat.opacity = 0.9;

    const mesh = new THREE.Mesh(tracerGeometry, mat);
    mesh.position.copy(aircraft.position).addScaledVector(_forward, 8);
    mesh.lookAt(mesh.position.clone().add(dir));
    mesh.frustumCulled = false;

    this.scene.add(mesh);
    this.tracers.push({ mesh, velocity, age: 0 });
  }

  // ── Missiles (full local simulation) ────────────────────

  onMissileLaunched(event: MissileLaunchEvent): void {
    const mat = materialPool.acquire();
    mat.color.set(0xff4444);
    const mesh = new THREE.Mesh(missileGeometry, mat);
    mesh.position.set(event.position.x, event.position.y, event.position.z);
    mesh.frustumCulled = false;

    // Add glow
    const glow = new THREE.PointLight(0xff2200, 3, 100);
    mesh.add(glow);

    this.scene.add(mesh);

    this.missiles.push({
      id: event.missileId,
      mesh,
      targetId: event.targetId,
      direction: new THREE.Vector3(event.direction.x, event.direction.y, event.direction.z),
      age: 0,
      decoyed: false,
    });
  }

  onMissileExploded(missileId: string): void {
    const idx = this.missiles.findIndex((m) => m.id === missileId);
    if (idx !== -1) {
      this.removeMissile(idx);
    }
  }

  private updateMissiles(dt: number): void {
    const remotes = this.remotePlayerManager.getRemotes();

    for (let i = this.missiles.length - 1; i >= 0; i--) {
      const m = this.missiles[i];
      m.age += dt;

      // Lifetime expired
      if (m.age > MISSILE_LIFETIME) {
        this.explosions?.spawn(m.mesh.position.clone());
        this.removeMissile(i);
        continue;
      }

      // Flare check — if a nearby flare exists, the missile gets decoyed
      if (!m.decoyed && this.flareSystem) {
        if (this.flareSystem.checkMissileHit(m.mesh.position)) {
          m.decoyed = true;
          // Missile destroyed by flare
          this.removeMissile(i);
          continue;
        }
      }

      // Track toward target
      if (!m.decoyed) {
        let targetPos: THREE.Vector3 | null = null;

        if (m.targetId === this.localPlayerId && this.localPlayerMesh) {
          targetPos = this.localPlayerMesh.position;
        } else {
          const targetEntry = remotes.get(m.targetId);
          if (targetEntry && targetEntry.mesh.visible) {
            targetPos = targetEntry.mesh.position;
          }
        }

        if (targetPos) {
          _toTarget.copy(targetPos).sub(m.mesh.position).normalize();
          const maxTurn = MISSILE_TURN_RATE * dt;
          m.direction.lerp(_toTarget, Math.min(maxTurn, 1));
          m.direction.normalize();
        }
      }

      // Move
      m.mesh.position.addScaledVector(m.direction, MISSILE_SPEED * dt);
      _lookTarget.copy(m.mesh.position).add(m.direction);
      m.mesh.lookAt(_lookTarget);

      // Hit detection against local player
      if (m.targetId === this.localPlayerId && this.localPlayerMesh) {
        const dist = m.mesh.position.distanceTo(this.localPlayerMesh.position);
        if (dist < MISSILE_HIT_RADIUS) {
          this.explosions?.spawn(m.mesh.position.clone());
          this.callbacks?.onMissileHitLocal(m.id, m.mesh.position.clone());
          this.removeMissile(i);
          continue;
        }
      }
    }
  }

  private removeMissile(index: number): void {
    const m = this.missiles[index];
    this.scene.remove(m.mesh);
    materialPool.release(m.mesh.material as THREE.MeshBasicMaterial);
    this.missiles.splice(index, 1);
  }

  /**
   * Returns positions of all remote missiles targeting the local player.
   */
  getMissilesTargetingLocal(): THREE.Vector3[] {
    const positions: THREE.Vector3[] = [];
    for (const m of this.missiles) {
      if (m.targetId === this.localPlayerId && !m.decoyed) {
        positions.push(m.mesh.position.clone());
      }
    }
    return positions;
  }

  /**
   * Count of missiles targeting the local player.
   */
  getIncomingMissileCount(): number {
    return this.missiles.filter((m) => m.targetId === this.localPlayerId && !m.decoyed).length;
  }

  dispose(): void {
    for (const t of this.tracers) {
      this.scene.remove(t.mesh);
      materialPool.release(t.mesh.material as THREE.MeshBasicMaterial);
    }
    this.tracers.length = 0;

    for (const m of this.missiles) {
      this.scene.remove(m.mesh);
      materialPool.release(m.mesh.material as THREE.MeshBasicMaterial);
    }
    this.missiles.length = 0;
  }
}
